import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetEMSSetpointsQuery,
  useGetEMSMemberEnergyQuery,
} from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

const CHARGE_COLOR = "var(--success)";
const DISCHARGE_COLOR = "var(--warning)";

/* Chart 2 — community battery power: total charge (above 0) and discharge
   (below 0) across all batteries, as diverging stacked columns. */
function PromoterLiquidBatChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: setpointsResp, isLoading } = useGetEMSSetpointsQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 20000, offset: 0 },
    { skip: !communityId }
  );
  // the setpoints endpoint returns { data: [...], total_count } (not a bare array)
  const setpoints = useMemo(
    () => (Array.isArray(setpointsResp?.data) ? setpointsResp.data : []),
    [setpointsResp]
  );

  // storage resource ids from the soc_bat metadata keys (reliable — excludes EV
  // and net-meter setpoints without depending on the resources query's `type`).
  const { data: memberEnergy = [] } = useGetEMSMemberEnergyQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 5000, offset: 0 },
    { skip: !communityId }
  );
  const storageIds = useMemo(() => {
    const s = new Set();
    memberEnergy.forEach((r) =>
      Object.keys(r.metadata?.soc_bat || {}).forEach((id) => s.add(String(id)))
    );
    return s;
  }, [memberEnergy]);

  const batSetpoints = useMemo(
    () => setpoints.filter((sp) => sp.resource_id && storageIds.has(String(sp.resource_id))),
    [setpoints, storageIds]
  );

  const hasData = !isLoading && batSetpoints.length > 0;
  const isEmpty = !isLoading && !hasData;
  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (!hasData) return { series: [], labels: [] };
    const chg = new Map();
    const dis = new Map();
    const tsSet = new Set();
    batSetpoints.forEach((sp) => {
      const ts = sp.datetime_timestamp;
      if (!ts) return;
      tsSet.add(ts);
      const v = Math.abs(sp.setpoint_value || 0);
      if (sp.setpoint_type === "CONSUME") chg.set(ts, (chg.get(ts) || 0) + v);
      else if (sp.setpoint_type === "INJECT") dis.set(ts, (dis.get(ts) || 0) + v);
    });
    const sorted = [...tsSet].sort((a, b) => new Date(a) - new Date(b));
    return {
      labels: sorted.map((ts) => createChartXAxisLabel(ts, "MIN15")),
      series: [
        {
          name: t("unified_ems.battery_charge"),
          data: sorted.map((ts) => Number((chg.get(ts) || 0).toFixed(3))),
          color: CHARGE_COLOR,
        },
        {
          name: t("unified_ems.battery_discharge"),
          data: sorted.map((ts) => -Number((dis.get(ts) || 0).toFixed(3))),
          color: DISCHARGE_COLOR,
        },
      ],
    };
  }, [batSetpoints, hasData, t]);

  if (isLoading || isEmpty) return null;
  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.promoter_liquid_bat_chart_title")}
      sub={t("unified_ems.promoter_liquid_bat_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      height={420}
      type="bar"
      unit="kW"
      yTitle="kW"
      stacked
    />
  );
}

PromoterLiquidBatChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};
PromoterLiquidBatChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default PromoterLiquidBatChart;
